import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { pointToLabel } from "../go/coordinates";
import { colorToStone, indexOf } from "../go/rules";
import { BLACK, EMPTY, WHITE, type Board, type Color, type Point, type Stone } from "../go/types";
import type { GradeLabel, MoveAnalysis } from "../engine/types";
import type { ShadowMove } from "../engine/variation";

interface GoBoardProps {
  board: Board;
  size: number;
  toPlay: Color;
  tool: "play" | "black" | "white" | "erase";
  analysisMoves: MoveAnalysis[];
  shadowMoves: ShadowMove[];
  ownership?: number[];
  showTerritory?: boolean;
  lastMove?: Point | null;
  lastMoveGrade?: GradeLabel | null;
  onPoint: (point: Point) => void;
}

// Tint for each move grade, used for the small marker on the most recent move.
const GRADE_COLORS: Record<GradeLabel, string> = {
  Great: "#0d9469",
  Good: "#4f9d4a",
  Inaccuracy: "#caa53a",
  Mistake: "#d9772f",
  Blunder: "#c8483a"
};

interface Metrics {
  canvasSize: number;
  padding: number;
  cell: number;
}

function starPoints(size: number): Point[] {
  if (size === 19) {
    return [3, 9, 15].flatMap((x) => [3, 9, 15].map((y) => ({ x, y })));
  }
  if (size === 13) {
    return [3, 6, 9].flatMap((x) => [3, 6, 9].map((y) => ({ x, y })));
  }
  if (size === 9) {
    return [2, 4, 6].flatMap((x) => [2, 4, 6].map((y) => ({ x, y })));
  }
  return [];
}

function stoneForTool(tool: GoBoardProps["tool"], toPlay: Color): Stone {
  if (tool === "black") return BLACK;
  if (tool === "white") return WHITE;
  if (tool === "play") return colorToStone(toPlay);
  return EMPTY;
}

function drawStone(ctx: CanvasRenderingContext2D, x: number, y: number, radius: number, stone: Stone, alpha = 1) {
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2);

  if (stone === BLACK) {
    const gradient = ctx.createRadialGradient(x - radius * 0.35, y - radius * 0.35, radius * 0.2, x, y, radius);
    gradient.addColorStop(0, "#4b5560");
    gradient.addColorStop(0.55, "#14171a");
    gradient.addColorStop(1, "#030405");
    ctx.fillStyle = gradient;
    ctx.fill();
    ctx.strokeStyle = "rgba(0, 0, 0, 0.5)";
  } else if (stone === WHITE) {
    const gradient = ctx.createRadialGradient(x - radius * 0.35, y - radius * 0.35, radius * 0.2, x, y, radius);
    gradient.addColorStop(0, "#ffffff");
    gradient.addColorStop(0.65, "#eee9df");
    gradient.addColorStop(1, "#c8c0b4");
    ctx.fillStyle = gradient;
    ctx.fill();
    ctx.strokeStyle = "rgba(57, 44, 30, 0.45)";
  }

  ctx.lineWidth = Math.max(1, radius * 0.07);
  ctx.stroke();
  ctx.restore();
}

function drawShadowMove(ctx: CanvasRenderingContext2D, x: number, y: number, radius: number, move: ShadowMove) {
  const stone = colorToStone(move.color);
  drawStone(ctx, x, y, radius, stone, 0.56);

  ctx.save();
  ctx.globalAlpha = 0.92;
  ctx.beginPath();
  ctx.arc(x, y, Math.max(7, radius * 0.42), 0, Math.PI * 2);
  ctx.fillStyle = stone === BLACK ? "rgba(255, 250, 240, 0.82)" : "rgba(25, 33, 42, 0.82)";
  ctx.fill();
  ctx.strokeStyle = stone === BLACK ? "rgba(10, 13, 17, 0.28)" : "rgba(255, 250, 240, 0.45)";
  ctx.lineWidth = Math.max(1, radius * 0.06);
  ctx.stroke();
  ctx.fillStyle = stone === BLACK ? "#111418" : "#fffaf0";
  ctx.font = `800 ${Math.max(9, radius * 0.56)}px ui-sans-serif, system-ui`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(String(move.moveNumber), x, y + 0.3);
  ctx.restore();
}

function drawTerritory(ctx: CanvasRenderingContext2D, ownership: number[], size: number, padding: number, cell: number) {
  const half = cell * 0.46;
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const value = ownership[y * size + x];
      if (value === undefined || Math.abs(value) < 0.15) continue; // skip contested/neutral points
      const alpha = Math.min(0.5, Math.abs(value) * 0.55);
      ctx.fillStyle = value > 0 ? `rgba(8, 10, 12, ${alpha})` : `rgba(252, 250, 245, ${alpha})`;
      ctx.fillRect(padding + x * cell - half, padding + y * cell - half, half * 2, half * 2);
    }
  }
}

function drawBoard(
  canvas: HTMLCanvasElement,
  board: Board,
  size: number,
  toPlay: Color,
  tool: GoBoardProps["tool"],
  analysisMoves: MoveAnalysis[],
  shadowMoves: ShadowMove[],
  hoverPoint: Point | null,
  ownership: number[] | undefined,
  showTerritory: boolean,
  lastMove: Point | null,
  lastMoveGrade: GradeLabel | null
): Metrics {
  const cssSize = canvas.clientWidth || 640;
  const dpr = window.devicePixelRatio || 1;
  canvas.width = Math.floor(cssSize * dpr);
  canvas.height = Math.floor(cssSize * dpr);

  const ctx = canvas.getContext("2d")!;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, cssSize, cssSize);

  const padding = Math.max(26, Math.min(38, cssSize * 0.058));
  const cell = (cssSize - padding * 2) / (size - 1);
  const stoneRadius = Math.min(cell * 0.46, cssSize * 0.032);

  const wood = ctx.createLinearGradient(0, 0, cssSize, cssSize);
  wood.addColorStop(0, "#e8c783");
  wood.addColorStop(0.52, "#d8aa5c");
  wood.addColorStop(1, "#be8544");
  ctx.fillStyle = wood;
  ctx.fillRect(0, 0, cssSize, cssSize);

  ctx.strokeStyle = "rgba(55, 36, 13, 0.78)";
  ctx.lineWidth = Math.max(1, cssSize * 0.0022);
  ctx.beginPath();
  for (let i = 0; i < size; i += 1) {
    const p = padding + i * cell;
    ctx.moveTo(padding, p);
    ctx.lineTo(cssSize - padding, p);
    ctx.moveTo(p, padding);
    ctx.lineTo(p, cssSize - padding);
  }
  ctx.stroke();

  ctx.fillStyle = "rgba(45, 28, 9, 0.82)";
  for (const point of starPoints(size)) {
    ctx.beginPath();
    ctx.arc(padding + point.x * cell, padding + point.y * cell, Math.max(2.6, cell * 0.095), 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.fillStyle = "rgba(47, 34, 20, 0.7)";
  ctx.font = `${Math.max(9, Math.min(12, cssSize * 0.018))}px ui-sans-serif, system-ui`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  for (let i = 0; i < size; i += 1) {
    const label = pointToLabel({ x: i, y: size - 1 }, size).replace(/\d+/, "");
    const number = String(size - i);
    const p = padding + i * cell;
    ctx.fillText(label, p, padding * 0.42);
    ctx.fillText(label, p, cssSize - padding * 0.42);
    ctx.fillText(number, padding * 0.42, p);
    ctx.fillText(number, cssSize - padding * 0.42, p);
  }

  if (showTerritory && ownership) {
    drawTerritory(ctx, ownership, size, padding, cell);
  }

  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const stone = board[indexOf({ x, y }, size)];
      if (stone !== EMPTY) {
        drawStone(ctx, padding + x * cell, padding + y * cell, stoneRadius, stone);
      }
    }
  }

  // Mark the most recent move with a small colored dot reflecting its grade (or neutral if ungraded).
  if (lastMove) {
    const cx = padding + lastMove.x * cell;
    const cy = padding + lastMove.y * cell;
    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, cy, Math.max(3, stoneRadius * 0.28), 0, Math.PI * 2);
    ctx.fillStyle = lastMoveGrade ? GRADE_COLORS[lastMoveGrade] : "rgba(220, 60, 50, 0.95)";
    ctx.fill();
    ctx.lineWidth = Math.max(1, stoneRadius * 0.08);
    ctx.strokeStyle = "rgba(255, 250, 240, 0.85)";
    ctx.stroke();
    ctx.restore();
  }

  analysisMoves
    .filter((move) => move.point)
    .slice(0, 8)
    .forEach((move, index) => {
      const point = move.point!;
      const x = padding + point.x * cell;
      const y = padding + point.y * cell;
      const markerRadius = Math.max(10, stoneRadius * 0.58);
      ctx.save();
      ctx.beginPath();
      ctx.arc(x, y, markerRadius, 0, Math.PI * 2);
      ctx.fillStyle = index === 0 ? "rgba(13, 125, 116, 0.94)" : "rgba(30, 91, 142, 0.9)";
      ctx.fill();
      ctx.lineWidth = 2;
      ctx.strokeStyle = "#fff9ed";
      ctx.stroke();
      ctx.fillStyle = "#fffdf7";
      ctx.font = `700 ${Math.max(10, markerRadius * 0.92)}px ui-sans-serif, system-ui`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(String(index + 1), x, y + 0.4);
      ctx.restore();
    });

  for (const move of shadowMoves) {
    const x = padding + move.point.x * cell;
    const y = padding + move.point.y * cell;
    drawShadowMove(ctx, x, y, stoneRadius, move);
  }

  if (hoverPoint) {
    const hoverStone = stoneForTool(tool, toPlay);
    const x = padding + hoverPoint.x * cell;
    const y = padding + hoverPoint.y * cell;
    if (tool === "erase") {
      ctx.save();
      ctx.strokeStyle = "rgba(145, 49, 37, 0.78)";
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(x - stoneRadius * 0.55, y - stoneRadius * 0.55);
      ctx.lineTo(x + stoneRadius * 0.55, y + stoneRadius * 0.55);
      ctx.moveTo(x + stoneRadius * 0.55, y - stoneRadius * 0.55);
      ctx.lineTo(x - stoneRadius * 0.55, y + stoneRadius * 0.55);
      ctx.stroke();
      ctx.restore();
    } else {
      drawStone(ctx, x, y, stoneRadius, hoverStone, 0.48);
    }
  }

  return { canvasSize: cssSize, padding, cell };
}

export function GoBoard({
  board,
  size,
  toPlay,
  tool,
  analysisMoves,
  shadowMoves,
  ownership,
  showTerritory = false,
  lastMove = null,
  lastMoveGrade = null,
  onPoint
}: GoBoardProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const metricsRef = useRef<Metrics | null>(null);
  const [canvasSize, setCanvasSize] = useState(640);
  const [hoverPoint, setHoverPoint] = useState<Point | null>(null);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const resize = () => {
      const width = container.getBoundingClientRect().width;
      setCanvasSize(Math.max(300, Math.floor(width)));
    };

    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    metricsRef.current = drawBoard(
      canvas,
      board,
      size,
      toPlay,
      tool,
      analysisMoves,
      shadowMoves,
      hoverPoint,
      ownership,
      showTerritory,
      lastMove,
      lastMoveGrade
    );
  }, [
    analysisMoves,
    board,
    canvasSize,
    hoverPoint,
    lastMove,
    lastMoveGrade,
    ownership,
    shadowMoves,
    showTerritory,
    size,
    toPlay,
    tool
  ]);

  function eventToPoint(event: React.PointerEvent<HTMLCanvasElement> | React.MouseEvent<HTMLCanvasElement>): Point | null {
    const canvas = canvasRef.current;
    const metrics = metricsRef.current;
    if (!canvas || !metrics) return null;

    const rect = canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    const gridX = Math.round((x - metrics.padding) / metrics.cell);
    const gridY = Math.round((y - metrics.padding) / metrics.cell);

    if (gridX < 0 || gridY < 0 || gridX >= size || gridY >= size) {
      return null;
    }

    const snapX = metrics.padding + gridX * metrics.cell;
    const snapY = metrics.padding + gridY * metrics.cell;
    const snapDistance = Math.hypot(x - snapX, y - snapY);
    return snapDistance <= metrics.cell * 0.48 ? { x: gridX, y: gridY } : null;
  }

  return (
    <div className="board-shell" ref={containerRef}>
      <canvas
        ref={canvasRef}
        className="go-board"
        style={{ width: canvasSize, height: canvasSize }}
        aria-label={`${size} by ${size} Go board`}
        onPointerMove={(event) => setHoverPoint(eventToPoint(event))}
        onPointerLeave={() => setHoverPoint(null)}
        onClick={(event) => {
          const point = eventToPoint(event);
          if (point) onPoint(point);
        }}
      />
    </div>
  );
}
